import React, { useState } from 'react';
import {
  Modal,
  Pressable,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { fpColors } from '../../../../styles/theme';
import type { PickerMode } from './buildPickerSections';

/**
 * ViewSwitcher — FE-10.1.6 picker view-mode menu.
 *
 * Tiny `[⋯ change view]` affordance that opens a popup menu listing
 * the available grouping modes. Stateless — emits `onChange(mode)`
 * to the parent.
 *
 * Three modes shipped in v1:
 *   - 'scenes'   default; group by source scan (highest leverage)
 *   - 'recent'   flat list — Sprint 3 polish back-compat
 *   - 'category' bucket by item_category
 *
 * Date mode is deferred until backend Gap A (#~110) exposes per-item
 * created_at on InventoryItemResponse.
 */

interface ViewSwitcherProps {
  current: PickerMode;
  onChange: (mode: PickerMode) => void;
}

interface OptionDef {
  mode: PickerMode;
  label: string;
  icon: keyof typeof MaterialIcons.glyphMap;
  description: string;
}

const OPTIONS: OptionDef[] = [
  {
    mode: 'scenes',
    label: 'Snapshots',
    icon: 'photo-library',
    description: 'Group by source scan',
  },
  {
    mode: 'recent',
    label: 'Recent',
    icon: 'access-time',
    description: 'Flat list, newest first',
  },
  {
    mode: 'category',
    label: 'Category',
    icon: 'label',
    description: 'Bucket by item category',
  },
];

export const ViewSwitcher: React.FC<ViewSwitcherProps> = ({
  current,
  onChange,
}) => {
  const [open, setOpen] = useState(false);
  const currentLabel =
    OPTIONS.find((o) => o.mode === current)?.label ?? 'Snapshots';

  return (
    <>
      <Pressable
        onPress={() => setOpen(true)}
        accessibilityRole="button"
        accessibilityLabel="Change view mode"
        style={({ pressed }) => [
          styles.trigger,
          pressed && { opacity: 0.85 },
        ]}
        testID="picker-view-switcher"
      >
        <Text style={styles.triggerText}>By {currentLabel.toLowerCase()}</Text>
        <MaterialIcons name="more-horiz" size={18} color={fpColors.outline} />
      </Pressable>

      <Modal
        visible={open}
        transparent
        animationType="fade"
        onRequestClose={() => setOpen(false)}
      >
        <Pressable
          style={styles.backdrop}
          onPress={() => setOpen(false)}
          accessibilityLabel="Close menu"
        >
          <View style={styles.menu} testID="picker-view-switcher-menu">
            {OPTIONS.map((opt) => {
              const selected = opt.mode === current;
              return (
                <Pressable
                  key={opt.mode}
                  onPress={() => {
                    onChange(opt.mode);
                    setOpen(false);
                  }}
                  accessibilityRole="menuitem"
                  accessibilityState={{ selected }}
                  accessibilityLabel={opt.label}
                  style={({ pressed }) => [
                    styles.option,
                    selected && styles.optionSelected,
                    pressed && { opacity: 0.85 },
                  ]}
                  testID={`picker-view-option-${opt.mode}`}
                >
                  <MaterialIcons
                    name={opt.icon}
                    size={20}
                    color={selected ? fpColors.primary : fpColors.onSurface}
                  />
                  <View style={{ flex: 1 }}>
                    <Text
                      style={[
                        styles.optionLabel,
                        selected && styles.optionLabelSelected,
                      ]}
                    >
                      {opt.label}
                    </Text>
                    <Text style={styles.optionDescription}>
                      {opt.description}
                    </Text>
                  </View>
                  {selected ? (
                    <MaterialIcons
                      name="check"
                      size={18}
                      color={fpColors.primary}
                    />
                  ) : null}
                </Pressable>
              );
            })}
          </View>
        </Pressable>
      </Modal>
    </>
  );
};

const styles = StyleSheet.create({
  trigger: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 8,
  },
  triggerText: {
    fontFamily: 'Inter_600SemiBold',
    fontSize: 13,
    color: fpColors.primary,
  },
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(28, 23, 33, 0.40)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  menu: {
    width: 280,
    backgroundColor: fpColors.surface,
    borderRadius: 14,
    paddingVertical: 6,
    elevation: 6,
    shadowColor: '#000',
    shadowOpacity: 0.15,
    shadowRadius: 14,
    shadowOffset: { width: 0, height: 4 },
  },
  option: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingVertical: 12,
    paddingHorizontal: 14,
  },
  optionSelected: {
    backgroundColor: fpColors.surfaceContainerLow,
  },
  optionLabel: {
    fontFamily: 'Inter_600SemiBold',
    fontSize: 15,
    color: fpColors.onSurface,
  },
  optionLabelSelected: {
    color: fpColors.primary,
  },
  optionDescription: {
    fontFamily: 'Inter_400Regular',
    fontSize: 12,
    color: fpColors.outline,
    marginTop: 2,
  },
});

export default ViewSwitcher;
